import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Zap, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TOOL_LIST } from "@/lib/tools";
import { useLang } from "@/i18n";
import { Examples } from "@/components/Examples";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/65edb782-a87a-4a9d-aa11-3033fbcee5a0/images/1007e4f278a464d670e45a487eb6970959f123f1efe058938e0f473348d1c975.jpeg";
const SHOP_IMG = "https://images.pexels.com/photos/6621020/pexels-photo-6621020.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PERSONAL_IMG = "https://static.prod-images.emergentagent.com/jobs/65edb782-a87a-4a9d-aa11-3033fbcee5a0/images/77d98c5d17e0186dd21ac52c1075f02a45028b2870b942bfb1e2fc5515abc97f.jpeg";
const FEATURE_ICONS = [Zap, Layers, ShieldCheck];

const Hero = () => {
  const { t, href } = useLang();
  return (
    <section className="grid-paper relative overflow-hidden border-b border-slate-200">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 md:grid-cols-12 lg:px-8 lg:py-28">
        <div className="rise md:col-span-6">
          <Link to="/entrar?modo=cadastro" data-testid="hero-free-badge" className="inline-flex items-center gap-3 rounded-full bg-[#FFB800] py-1.5 pl-1.5 pr-4 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/20 transition-transform hover:-translate-y-0.5">
            <span className="rounded-full bg-slate-900 px-3 py-1 font-mono text-xs text-white">{t("free.badgeNum")}</span>
            {t("free.badge")}
          </Link>
          <h1 className="mt-7 text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {t("landing.h1a")}{" "}<br />
            <span className="text-[#00A3E0]">{t("landing.h1b")}</span> {t("landing.h1c")}
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">{t("landing.sub")}</p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Button asChild className="h-12 rounded-full bg-slate-900 px-7 text-base hover:bg-slate-800" data-testid="hero-cta-button">
              <Link to="/entrar?modo=cadastro">{t("landing.cta")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Link to={href("pricing")} data-testid="hero-pricing-link" className="text-sm font-semibold text-slate-700 underline-offset-4 hover:underline">{t("landing.after")}</Link>
          </div>
          <p className="mt-4 text-sm text-slate-500" data-testid="hero-free-note">{t("free.note")}</p>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs text-slate-500">
            {t("landing.checks").map((c) => (
              <span key={c} className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" />{c}</span>
            ))}
          </div>
        </div>
        <div className="rise md:col-span-6" style={{ animationDelay: "0.15s" }}>
          <div className="crop-marks relative">
            <img src={HERO_IMG} alt={t("landing.imgAlt")} className="aspect-[4/3] w-full rounded-2xl object-cover shadow-2xl shadow-slate-900/10" />
            <div className="absolute -bottom-6 -left-4 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur sm:-left-8">
              <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{t("landing.output")}</p>
              <p className="mt-1 font-display text-lg font-semibold text-slate-900">A4 · 300 DPI</p>
              <p className="font-mono text-xs text-[#E40046]">PNG · JPG · PDF · CMYK</p>
            </div>
            <div className="absolute -right-3 -top-5 grid h-28 w-28 rotate-12 place-items-center rounded-full bg-[#E40046] text-center text-white shadow-xl sm:-right-6" data-testid="hero-free-sticker">
              <div>
                <p className="font-display text-4xl font-bold leading-none">5</p>
                <p className="mt-1 px-2 text-[10px] font-bold uppercase leading-tight tracking-wide">{t("free.sticker")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const AudienceCard = ({ k, img, primary, to, testId }) => {
  const { t } = useLang();
  return (
    <div className={`rise flex flex-col overflow-hidden rounded-2xl border bg-white ${primary ? "border-slate-900 shadow-xl lg:col-span-7" : "border-slate-200 lg:col-span-5"}`} data-testid={testId}>
      {img && <img src={img} alt="" className={`w-full object-cover ${primary ? "h-56" : "h-48"}`} />}
      <div className="flex flex-1 flex-col p-8">
        <span className="w-fit rounded-full px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider text-white" style={{ background: primary ? "#00A3E0" : "#0F172A" }}>{t(`landing.${k}.tag`)}</span>
        <h3 className="mt-5 text-2xl font-semibold tracking-tight text-slate-900">{t(`landing.${k}.title`)}</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(`landing.${k}.text`)}</p>
        <ul className="mt-6 space-y-2.5">
          {t(`landing.${k}.bullets`).map((b) => <li key={b} className="flex gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{b}</li>)}
        </ul>
        <div className="mt-auto pt-8">
          <Button asChild variant={primary ? "default" : "outline"} className={`rounded-full ${primary ? "bg-slate-900 hover:bg-slate-800" : ""}`} data-testid={`${testId}-cta`}>
            <Link to={to}>{t(`landing.${k}.cta`)} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

const Audience = () => {
  const { t, href } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-24 sm:px-6 lg:px-8">
      <p className="font-mono text-xs font-semibold uppercase tracking-wider text-slate-400">{t("landing.audienceEyebrow")}</p>
      <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t("landing.audienceTitle")}</h2>
      <div className="mt-12 grid gap-6 lg:grid-cols-12">
        <AudienceCard k="personal" primary img={PERSONAL_IMG} to="/entrar?modo=cadastro" testId="audience-personal" />
        <AudienceCard k="shop" img={SHOP_IMG} to={href("pricing")} testId="audience-shop" />
      </div>
    </section>
  );
};

const Steps = () => {
  const { t } = useLang();
  return (
    <section className="border-y border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <p className="font-mono text-xs font-semibold uppercase tracking-wider text-slate-400">{t("landing.stepsEyebrow")}</p>
        <div className="mt-10 grid gap-10 md:grid-cols-3">
          {t("landing.steps").map((s, i) => (
            <div key={s.title} className="relative">
              <span className="font-display text-5xl font-bold" style={{ color: ["#00A3E0", "#E40046", "#FFB800"][i] }}>0{i + 1}</span>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ToolsGrid = () => {
  const { t, toolHref } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <p className="font-mono text-xs font-semibold uppercase tracking-wider text-slate-400">{t("landing.toolsEyebrow")}</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t("landing.toolsTitle")}</h2>
      </div>
      <div className="mt-14 grid gap-5 md:grid-cols-2">
        {TOOL_LIST.map((tl, i) => {
          const Icon = tl.icon;
          return (
            <Link
              key={tl.slug}
              to={toolHref(tl.slug)}
              data-testid={`landing-tool-card-${tl.slug}`}
              className="rise group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-slate-900 hover:shadow-xl"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <span className="absolute right-6 top-6 font-display text-6xl font-bold opacity-[0.07]" style={{ color: tl.color }}>{tl.tag}</span>
              <div className="grid h-12 w-12 place-items-center rounded-xl text-white" style={{ background: tl.color }}>
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">{t(`tools.${tl.slug}.name`)}</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">{t(`tools.${tl.slug}.description`)}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {t(`tools.${tl.slug}.badges`).map((b) => <span key={b} className="rounded-full bg-slate-100 px-3 py-1 font-mono text-[11px] text-slate-600">{b}</span>)}
              </div>
              <span className="mt-7 inline-flex items-center text-sm font-semibold text-slate-900">{t("landing.useTool")} <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const Features = () => {
  const { t } = useLang();
  return (
    <section className="border-y border-slate-200 bg-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 md:grid-cols-3 lg:px-8">
        {t("landing.features").map(({ title, text }, i) => {
          const Icon = FEATURE_ICONS[i];
          return (
            <div key={title}>
              <Icon className="h-6 w-6 text-[#00A3E0]" />
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const Cta = () => {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-16 sm:px-14">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#00A3E0] opacity-30 blur-3xl" />
        <div className="absolute -bottom-20 right-40 h-56 w-56 rounded-full bg-[#E40046] opacity-25 blur-3xl" />
        <h2 className="relative max-w-xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">{t("landing.ctaTitle")}</h2>
        <p className="relative mt-4 max-w-lg text-slate-300">{t("landing.ctaSub")}</p>
        <Button asChild className="relative mt-8 h-12 rounded-full bg-white px-7 text-base text-slate-900 hover:bg-slate-100" data-testid="cta-signup-button">
          <Link to="/entrar?modo=cadastro">{t("landing.ctaBtn")}</Link>
        </Button>
      </div>
    </section>
  );
};

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <Examples />
      <Audience />
      <ToolsGrid />
      <Steps />
      <Features />
      <Cta />
    </div>
  );
}
